import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { HostBridge } from './host.js';
import type { InboundEnvelope } from '@chirpback/protocol';
import { SurveySkeleton } from './_components/SurveySkeleton.js';
import { AnnouncementSkeleton } from './_components/AnnouncementSkeleton.js';
import { FeedbackSkeleton } from './_components/FeedbackSkeleton.js';
// Survey is the most-opened surface and is now part of the base shell bundle
// (see vite.config.ts manualChunks comment). Direct import — no Suspense gap.
import { SurveyRenderer } from './renderers/Survey/index.js';

// Announcement and Feedback stay lazy. They're prefetched during idle time
// from main.tsx so by the time a user opens one, the chunk is in the cache.
const AnnouncementRenderer = lazy(() => import('./renderers/Announcement/index.js').then((m) => ({ default: m.AnnouncementRenderer })));
const FeedbackRenderer = lazy(() => import('./renderers/FeedbackWidget/index.js').then((m) => ({ default: m.FeedbackRenderer })));

import type { AnnouncementLayout, AnnouncementOpenMode, AnnouncementPosition } from '@chirpback/protocol';

type Surface =
  | { kind: 'idle' }
  | {
      kind: 'survey';
      publicId: string;
      prefetched?: unknown;
      // When true, the SDK is fetching the payload in parallel with iframe
      // boot and will deliver it via chirp:survey:payload. The renderer must
      // suppress its own fetch until prefetched lands.
      awaitingPayload?: boolean;
      // Set when the SDK's parallel fetch failed (survey_not_found, network).
      // Renderer reads this and renders an inline error instead of spinning.
      errorState?: { code: string; message: string };
      source?: 'code' | 'urlAuto';
      // Forwarded from Chirp.survey.open(id, opts) — currently carries
      // `variables` (host-supplied map for piping/conditions), `force`, and
      // optional theme overrides. Propagated as opaque to the renderer.
      opts?: { variables?: Record<string, unknown>; force?: boolean };
      openedAt: number;
    }
  | {
      kind: 'announcement';
      mode: AnnouncementOpenMode;
      publicId?: string;
      collectionSlug?: string;
      layout?: AnnouncementLayout;
      position?: AnnouncementPosition;
      layoutSettings?: Record<string, unknown>;
      prefetched?: unknown;
      awaitingPayload?: boolean;
      openedAt: number;
    }
  | { kind: 'feedback'; config?: unknown; widget?: unknown; openedAt: number };

export interface ShellUser {
  uniqueId?: string;
  email?: string;
  fullName?: string;
  traits?: Record<string, unknown>;
}

export interface ShellHydration {
  projectId: string;
  // Public client_id (UUID) — sent as the x-chirp-client-id header on
  // GraphQL mutations so the API can resolve the project pre-bootstrap.
  clientId?: string;
  apiHost: string;
  sessionId?: string;
  anonymousId?: string;
  projectUserId?: string;
  host?: { url?: string; referrer?: string };
  user?: ShellUser;
}

// Standalone embed mode: shell is loaded via URL params instead of postMessage.
// Used by Chirp.embed.announcements() — renders the feed directly, no bridge.
interface EmbedStyles {
  background?: string;
  text?: string;
  textMuted?: string;
  primary?: string;
  border?: string;
  radius?: number;
  fontFamily?: string;
}

function useEmbedMode(): {
  isEmbed: boolean;
  hydration: ShellHydration | null;
  publicId?: string;
  showFilters: boolean;
  styles?: EmbedStyles;
} {
  return useMemo(() => {
    if (typeof window === 'undefined') return { isEmbed: false, hydration: null, showFilters: true };
    const params = new URLSearchParams(window.location.search);
    if (params.get('mode') !== 'embed') return { isEmbed: false, hydration: null, showFilters: true };
    const clientId = params.get('clientId') ?? '';
    const apiHost = params.get('apiHost') ?? '';
    if (!clientId || !apiHost) return { isEmbed: false, hydration: null, showFilters: true };
    let styles: EmbedStyles | undefined;
    try {
      const raw = params.get('styles');
      if (raw) styles = JSON.parse(raw) as EmbedStyles;
    } catch { /* ignore */ }

    // Identity passed via URL params by core/src/chirp.ts mountEmbedded
    // Changelog. We only build the `user` field when at least one is
    // present — bare anonymous callers leave it undefined, which matches
    // the public /public/<slug>/changelog flow.
    const urlEmail = params.get('email') ?? undefined;
    const urlUniqueId = params.get('uniqueId') ?? undefined;
    const user = urlEmail || urlUniqueId
      ? { email: urlEmail, uniqueId: urlUniqueId }
      : undefined;

    return {
      isEmbed: true,
      hydration: {
        projectId: clientId,
        apiHost: decodeURIComponent(apiHost),
        user,
      },
      publicId: params.get('publicId') ?? undefined,
      showFilters: params.get('filters') !== '0',
      styles,
    };
  }, []);
}

export const App: React.FC = () => {
  const embedMode = useEmbedMode();

  // If loaded in embed mode, skip the bridge entirely and render inline feed.
  if (embedMode.isEmbed && embedMode.hydration) {
    return (
      <Suspense fallback={<AnnouncementSkeleton layout="inline" />}>
        <EmbedApp
          hydration={embedMode.hydration}
          publicId={embedMode.publicId}
          showFilters={embedMode.showFilters}
          styles={embedMode.styles}
        />
      </Suspense>
    );
  }

  return <BridgeApp />;
};

const EmbedApp = lazy(() =>
  import('./renderers/Announcement/index.js').then((m) => ({
    default: function EmbedWrapper(props: { hydration: ShellHydration; publicId?: string; showFilters: boolean; styles?: EmbedStyles }) {
      const bridge = useMemo(() => new HostBridge(), []);
      useEffect(() => () => bridge.destroy(), [bridge]);
      return (
        <m.AnnouncementRenderer
          bridge={bridge}
          mode={props.publicId ? 'collection' : 'global'}
          publicId={props.publicId}
          layout="inline"
          layoutSettings={{ showFilters: props.showFilters, styles: props.styles }}
          hydration={props.hydration}
        />
      );
    },
  })),
);

const BridgeApp: React.FC = () => {
  const bridge = useMemo(() => new HostBridge(), []);
  const [surface, setSurface] = useState<Surface>({ kind: 'idle' });
  const [hydration, setHydration] = useState<ShellHydration | null>(null);

  // When the host calls bridge.hide() the iframe gets display:none, which
  // collapses our root element to 0×0. We reset surface to idle here so the
  // React tree unmounts the previous renderer. Without this, the next show()
  // flashes the previous surface for the frame between bridge.show() and the
  // new chirp:*:open message landing — the "last screen flicker" bug.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof ResizeObserver === 'undefined') return;
    const root = document.documentElement;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect && rect.width === 0 && rect.height === 0) {
        setSurface({ kind: 'idle' });
      }
    });
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    bridge.onReady();
    const unsubscribe = bridge.subscribe((message: InboundEnvelope) => {
      switch (message.type) {
        case 'chirp:hydrate':
          setHydration({
            projectId: message.payload.projectId,
            clientId: (message.payload as { clientId?: string }).clientId,
            apiHost: message.payload.apiHost,
            sessionId: message.payload.session?.sessionId,
            anonymousId: message.payload.session?.anonymousId,
            projectUserId: message.payload.session?.projectUserId,
            host: message.payload.host
              ? {
                  url: message.payload.host.url,
                  referrer: message.payload.host.referrer,
                }
              : undefined,
            user: message.payload.session?.uniqueId || message.payload.session?.traits
              ? {
                  uniqueId: message.payload.session?.uniqueId,
                  email: (message.payload.session?.traits as { email?: string } | undefined)?.email,
                  fullName: (message.payload.session?.traits as { fullName?: string } | undefined)?.fullName,
                  traits: message.payload.session?.traits as Record<string, unknown> | undefined,
                }
              : undefined,
          });
          break;
        case 'chirp:identify':
          setHydration((prev) => prev ? {
            ...prev,
            user: {
              uniqueId: message.payload.uniqueId,
              email: (message.payload.traits as { email?: string } | undefined)?.email,
              fullName: (message.payload.traits as { fullName?: string } | undefined)?.fullName,
              traits: message.payload.traits as Record<string, unknown> | undefined,
            },
          } : prev);
          break;
        case 'chirp:survey:open':
          setSurface({
            kind: 'survey',
            publicId: message.payload.publicId,
            prefetched: message.payload.prefetched,
            awaitingPayload: message.payload.awaitingPayload,
            source: message.payload.source,
            opts: message.payload.opts as
              | { variables?: Record<string, unknown>; force?: boolean }
              | undefined,
            openedAt: Date.now(),
          });
          break;
        case 'chirp:survey:payload':
          // Late-arriving payload from the SDK's parallel fetch. Update the
          // surface in place so the renderer (already mounted with skeleton)
          // hot-swaps in the real survey.
          setSurface((prev) =>
            prev.kind === 'survey' && prev.publicId === message.payload.publicId
              ? { ...prev, prefetched: message.payload.payload, awaitingPayload: false }
              : prev,
          );
          break;
        case 'chirp:survey:error':
          // Parallel fetch failed (survey_not_found, network). Flip the surface
          // out of awaitingPayload into an error state so the renderer can show
          // an inline message instead of hanging on the skeleton.
          setSurface((prev) =>
            prev.kind === 'survey' && prev.publicId === message.payload.publicId
              ? {
                  ...prev,
                  awaitingPayload: false,
                  errorState: { code: message.payload.code, message: message.payload.message },
                }
              : prev,
          );
          break;
        case 'chirp:announcement:open':
          setSurface({
            kind: 'announcement',
            mode: message.payload.mode ?? 'single',
            publicId: message.payload.publicId,
            collectionSlug: message.payload.collectionSlug,
            layout: message.payload.layout,
            position: message.payload.position,
            layoutSettings: message.payload.layoutSettings,
            prefetched: message.payload.prefetched,
            awaitingPayload: message.payload.awaitingPayload,
            openedAt: Date.now(),
          });
          break;
        case 'chirp:announcement:payload':
          setSurface((prev) =>
            prev.kind === 'announcement' && prev.publicId === message.payload.publicId
              ? { ...prev, prefetched: message.payload.payload, awaitingPayload: false }
              : prev,
          );
          break;
        case 'chirp:feedback:open':
          setSurface({ kind: 'feedback', config: message.payload.config, widget: message.payload.widget, openedAt: Date.now() });
          break;
        case 'chirp:survey:close':
        case 'chirp:announcement:close':
        case 'chirp:feedback:close':
          setSurface({ kind: 'idle' });
          break;
        case 'chirp:teardown':
          setSurface({ kind: 'idle' });
          setHydration(null);
          break;
        case 'chirp:host:keydown':
          // Re-dispatch a keydown on the iframe document so the renderer's own
          // Esc handler fires regardless of whether the user clicked into the
          // iframe first. Browsers don't propagate parent keydowns across
          // origin-isolated iframes, so we do it manually via the bridge.
          try {
            const synthetic = new KeyboardEvent('keydown', {
              key: message.payload.key,
              bubbles: true,
              cancelable: true,
            });
            document.dispatchEvent(synthetic);
          } catch {
            // older Safari / sandboxed contexts may reject the constructor — ignore
          }
          break;
        default:
          break;
      }
    });
    return () => {
      unsubscribe();
      bridge.destroy();
    };
  }, [bridge]);

  // Send chirp:perf:firstPaint once per surface open, after React commits.
  // The SDK uses this to compute tToFirstPaint for telemetry. useEffect runs
  // after the DOM is updated and right before the browser paints, which is
  // close enough to first paint for a perf delta.
  const surfaceOpenedAt = surface.kind === 'idle' ? 0 : surface.openedAt;
  useEffect(() => {
    if (surface.kind === 'idle') return;
    const hasContent =
      surface.kind === 'feedback'
        ? true
        : Boolean((surface as { prefetched?: unknown }).prefetched);
    bridge.send('chirp:perf:firstPaint', {
      surface: surface.kind,
      publicId: 'publicId' in surface ? surface.publicId : undefined,
      hasContent,
    });
  }, [surfaceOpenedAt]);

  if (surface.kind === 'idle' || !hydration) return null;

  // Pick a surface-aware skeleton so the user sees survey/announcement/feedback-shaped
  // DOM the same frame the open message arrives, before the lazy renderer chunk resolves.
  // The skeletons live in the base shell bundle (not lazy) so this is free.
  const skeleton =
    surface.kind === 'survey' ? <SurveySkeleton />
    : surface.kind === 'announcement' ? <AnnouncementSkeleton layout={surface.layout} position={surface.position} />
    : <FeedbackSkeleton />;

  return (
    <Suspense fallback={skeleton}>
      {surface.kind === 'survey' && (
        <SurveyRenderer
          key={`survey-${surface.publicId}-${surface.openedAt}`}
          bridge={bridge}
          publicId={surface.publicId}
          prefetched={surface.prefetched}
          awaitingPayload={surface.awaitingPayload}
          errorState={surface.errorState}
          source={surface.source}
          variableOverrides={surface.opts?.variables}
          hydration={hydration}
        />
      )}
      {surface.kind === 'announcement' && (
        <AnnouncementRenderer
          key={`announcement-${surface.mode}-${surface.publicId ?? surface.collectionSlug ?? 'global'}-${surface.openedAt}`}
          bridge={bridge}
          mode={surface.mode}
          publicId={surface.publicId}
          collectionSlug={surface.collectionSlug}
          layout={surface.layout}
          position={surface.position}
          layoutSettings={surface.layoutSettings}
          prefetched={surface.prefetched}
          hydration={hydration}
        />
      )}
      {surface.kind === 'feedback' && (
        <FeedbackRenderer
          key={`feedback-${surface.openedAt}`}
          bridge={bridge}
          config={surface.config}
          widget={surface.widget}
          hydration={hydration}
        />
      )}
    </Suspense>
  );
};
